import { useState } from "react";
import { ArrowLeft, Plus, Trash2, Eye, Download, Loader2, CheckCircle2, FilePlus2, Upload, Save, Bot } from "lucide-react";
import { toast } from "sonner";
import { invoiceGenGenerate, invoiceGenCheckDuplicate, invoiceGenUpdateRecord, invoiceGenDownloadRecord } from "@/lib/api";
import { computeTotals, money } from "./totals";
import InvoicePreview from "./InvoicePreview";

const STATUS_OPTIONS = ["Draft", "Unpaid", "Partial", "Paid"];

function Field({ label, missing, children }) {
  return (
    <div>
      <label className="ash-label flex items-center gap-2">
        {label}
        {missing && (
          <span className="text-[10px] uppercase tracking-wide bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded">
            Not found — verify
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

export default function EditInvoiceStep({ draft, setDraft, originalDraft, candidatePayments, editingRecordId, onBack, onClosed, onAskAI }) {
  const [mode, setMode] = useState("edit"); // 'edit' | 'preview'
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closed, setClosed] = useState(false);

  const missing = (path) => {
    const parts = path.split(".");
    let v = originalDraft;
    for (const p of parts) v = v?.[p];
    return !v;
  };

  const updateField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const updateItem = (idx, key, value) =>
    setDraft((d) => {
      const items = d.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
      return { ...d, items };
    });
  const addItem = () =>
    setDraft((d) => ({
      ...d,
      items: [
        ...d.items,
        { service_date: "", service_time: "", service_code: "", duration: "", therapist: "", notes: "", tax: 0, amount: 0 },
      ],
    }));
  const removeItem = (idx) => setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));

  const updatePayment = (idx, key, value) =>
    setDraft((d) => {
      const payments = d.payments.map((p, i) => (i === idx ? { ...p, [key]: value } : p));
      return { ...d, payments };
    });
  const addPayment = (prefill = {}) =>
    setDraft((d) => ({ ...d, payments: [...d.payments, { date: "", details: "", amount: 0, ...prefill }] }));
  const removePayment = (idx) => setDraft((d) => ({ ...d, payments: d.payments.filter((_, i) => i !== idx) }));

  const totals = computeTotals(draft.items, draft.payments);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      let finalDraft = draft;
      try {
        const dup = await invoiceGenCheckDuplicate(draft.invoice_number);
        if (dup?.exists) {
          const ok = window.confirm(
            `Invoice ${draft.invoice_number} was already generated on ${(dup.generated_at || "").slice(0, 10)} ` +
              `(total ${money(dup.total)}). Generate again anyway?`
          );
          if (!ok) {
            setDownloading(false);
            return;
          }
          finalDraft = { ...draft, force: true };
        }
      } catch {
        /* duplicate check is a soft UX helper — never block generation if it fails */
      }
      const blob = await invoiceGenGenerate(finalDraft);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeNo = (draft.invoice_number || "invoice").replace(/[^A-Za-z0-9-]/g, "");
      a.href = url;
      a.download = `ASHLAN_Invoice_${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded.");
      setClosed(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadExisting = async () => {
    setDownloading(true);
    try {
      const blob = await invoiceGenDownloadRecord(editingRecordId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeNo = (draft.invoice_number || "invoice").replace(/[^A-Za-z0-9-]/g, "");
      a.href = url;
      a.download = `ASHLAN_Invoice_${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const { invoice_number, ...editable } = draft;
      const res = await invoiceGenUpdateRecord(editingRecordId, editable);
      toast.success(`Changes saved to Invoice ${draft.invoice_number}.`);
      if (res.was_sent) {
        toast.warning("This invoice was already sent — the recipient won't see this update unless you resend it.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your changes.");
    } finally {
      setSaving(false);
    }
  };

  const downloadHandler = editingRecordId ? handleDownloadExisting : handleDownload;

  if (closed) {
    return (
      <div className="max-w-2xl" data-testid="invoice-gen-closed">
        <div className="border border-[#DCFCE7] bg-[#F0FDF4] rounded-lg p-6 flex items-start gap-3">
          <CheckCircle2 className="text-[#16A34A] shrink-0" size={22} />
          <div>
            <p className="font-semibold text-[#080808]">
              Invoice {draft.invoice_number} generated and downloaded.
            </p>
            <p className="text-sm text-[#6B7280] mt-1">
              It's been saved to Invoice History. Ready for the next one?
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button
            type="button"
            onClick={() => onClosed("select")}
            data-testid="invoice-gen-start-next"
            className="ash-btn-primary inline-flex items-center gap-1.5"
          >
            <FilePlus2 size={14} /> Start Next Invoice
          </button>
          <button
            type="button"
            onClick={() => onClosed("upload")}
            data-testid="invoice-gen-upload-different"
            className="inline-flex items-center gap-1.5 text-sm border border-[#D1D5DB] rounded-md px-3 py-2 hover:border-[#3A7A8C]"
          >
            <Upload size={14} /> Upload a Different Statement
          </button>
        </div>
      </div>
    );
  }

  if (mode === "preview") {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => setMode("edit")}
            data-testid="invoice-gen-back-to-edit"
            className="inline-flex items-center gap-1.5 text-sm text-[#3C4048] hover:text-[#080808]"
          >
            <ArrowLeft size={14} /> Edit
          </button>
          <button
            type="button"
            onClick={downloadHandler}
            disabled={downloading}
            data-testid="invoice-gen-download-button"
            className="ash-btn-primary ml-auto inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PDF
          </button>
        </div>
        {/* eslint-disable-next-line */}
        <InvoicePreview draft={draft} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          data-testid="invoice-gen-back-to-select"
          className="inline-flex items-center gap-1.5 text-sm text-[#3C4048] hover:text-[#080808]"
        >
          <ArrowLeft size={14} /> {editingRecordId ? "Back to Invoice History" : "Choose a different invoice"}
        </button>
        <div className="ml-auto flex items-center gap-3">
          {editingRecordId && onAskAI && (
            <button
              type="button"
              onClick={() => onAskAI(draft.invoice_number)}
              data-testid="invoice-gen-ask-ai-button"
              className="inline-flex items-center gap-1.5 text-sm border border-[#5FA8B4]/50 text-[#3A7A8C] rounded-md px-3 py-2 hover:bg-[#5FA8B4]/5"
            >
              <Bot size={14} /> Ask AI About This Invoice
            </button>
          )}
          <button
            type="button"
            onClick={() => setMode("preview")}
            data-testid="invoice-gen-preview-button"
            className="inline-flex items-center gap-1.5 text-sm border border-[#D1D5DB] rounded-md px-3 py-2 hover:border-[#3A7A8C]"
          >
            <Eye size={14} /> Preview Invoice
          </button>
          {editingRecordId ? (
            <>
              <button
                type="button"
                onClick={handleDownloadExisting}
                disabled={downloading}
                data-testid="invoice-gen-download-button-edit"
                className="inline-flex items-center gap-1.5 text-sm border border-[#D1D5DB] rounded-md px-3 py-2 hover:border-[#3A7A8C] disabled:opacity-60"
              >
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PDF
              </button>
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={saving}
                data-testid="invoice-gen-save-changes-button"
                className="ash-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              data-testid="invoice-gen-download-button-edit"
              className="ash-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PDF
            </button>
          )}
        </div>
      </div>

      {editingRecordId && (
        <div className="mb-6 text-xs uppercase tracking-wide text-[#3A7A8C] font-semibold" data-testid="invoice-gen-editing-badge">
          Editing Invoice {draft.invoice_number}
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Field label="Invoice Number">
          <input className="ash-input bg-[#F3F4F6]" value={draft.invoice_number} disabled data-testid="invoice-field-number" />
        </Field>
        <Field label="Invoice Date" missing={missing("invoice_date")}>
          <input
            className="ash-input"
            value={draft.invoice_date || ""}
            onChange={(e) => updateField("invoice_date", e.target.value)}
            data-testid="invoice-field-date"
          />
        </Field>
        <Field label="Status" missing={!draft.status}>
          <select
            className="ash-input"
            value={draft.status || ""}
            onChange={(e) => updateField("status", e.target.value)}
            data-testid="invoice-field-status"
          >
            <option value="">— Select —</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
      </section>

      <section className="mb-8">
        <p className="text-sm font-semibold text-[#080808] mb-3">Bill To</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Client / Company Name" missing={missing("bill_to_name")}>
            <input className="ash-input" value={draft.bill_to_name || ""} onChange={(e) => updateField("bill_to_name", e.target.value)} data-testid="invoice-field-bill-name" />
          </Field>
          <Field label="Address" missing={missing("bill_to_address")}>
            <input className="ash-input" value={draft.bill_to_address || ""} onChange={(e) => updateField("bill_to_address", e.target.value)} data-testid="invoice-field-bill-address" />
          </Field>
          <Field label="Telephone" missing={missing("bill_to_phone")}>
            <input className="ash-input" value={draft.bill_to_phone || ""} onChange={(e) => updateField("bill_to_phone", e.target.value)} data-testid="invoice-field-bill-phone" />
          </Field>
          <Field label="Email" missing={missing("bill_to_email")}>
            <input className="ash-input" value={draft.bill_to_email || ""} onChange={(e) => updateField("bill_to_email", e.target.value)} data-testid="invoice-field-bill-email" />
          </Field>
        </div>
      </section>

      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-[#080808]">Service Line Items</p>
          <button type="button" onClick={addItem} data-testid="invoice-gen-add-item" className="inline-flex items-center gap-1 text-xs text-[#3A7A8C] hover:text-[#080808]">
            <Plus size={14} /> Add line item
          </button>
        </div>
        <div className="flex flex-col gap-6">
          {draft.items.map((it, idx) => (
            <div key={idx} className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`invoice-item-${idx}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Service Date" missing={missing(`items.${idx}.service_date`)}>
                  <input className="ash-input" value={it.service_date || ""} onChange={(e) => updateItem(idx, "service_date", e.target.value)} data-testid={`invoice-item-${idx}-date`} />
                </Field>
                <Field label="Time" missing={missing(`items.${idx}.service_time`)}>
                  <input className="ash-input" value={it.service_time || ""} onChange={(e) => updateItem(idx, "service_time", e.target.value)} data-testid={`invoice-item-${idx}-time`} />
                </Field>
                <Field label="Service Code" missing={missing(`items.${idx}.service_code`)}>
                  <input className="ash-input" value={it.service_code || ""} onChange={(e) => updateItem(idx, "service_code", e.target.value)} data-testid={`invoice-item-${idx}-code`} />
                </Field>
                <Field label="Duration" missing={missing(`items.${idx}.duration`)}>
                  <input className="ash-input" value={it.duration || ""} onChange={(e) => updateItem(idx, "duration", e.target.value)} data-testid={`invoice-item-${idx}-duration`} />
                </Field>
                <Field label="Therapist / Provider" missing={missing(`items.${idx}.therapist`)}>
                  <input className="ash-input" value={it.therapist || ""} onChange={(e) => updateItem(idx, "therapist", e.target.value)} data-testid={`invoice-item-${idx}-therapist`} />
                </Field>
                <Field label="Tax">
                  <input type="number" step="0.01" className="ash-input" value={it.tax ?? 0} onChange={(e) => updateItem(idx, "tax", parseFloat(e.target.value) || 0)} data-testid={`invoice-item-${idx}-tax`} />
                </Field>
                <Field label="Amount" missing={missing(`items.${idx}.amount`)}>
                  <input type="number" step="0.01" className="ash-input" value={it.amount ?? ""} onChange={(e) => updateItem(idx, "amount", parseFloat(e.target.value) || 0)} data-testid={`invoice-item-${idx}-amount`} />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Description / Notes" missing={missing(`items.${idx}.notes`)}>
                  <textarea
                    className="ash-input min-h-[80px]"
                    value={it.notes || ""}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    data-testid={`invoice-item-${idx}-notes`}
                  />
                </Field>
              </div>
              {draft.items.length > 1 && (
                <button type="button" onClick={() => removeItem(idx)} data-testid={`invoice-item-${idx}-remove`} className="mt-2 inline-flex items-center gap-1 text-xs text-[#B91C1C] hover:underline">
                  <Trash2 size={12} /> Remove item
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-[#080808]">Payments</p>
          <button type="button" onClick={() => addPayment()} data-testid="invoice-gen-add-payment" className="inline-flex items-center gap-1 text-xs text-[#3A7A8C] hover:text-[#080808]">
            <Plus size={14} /> Add payment
          </button>
        </div>
        {candidatePayments?.length > 0 && (
          <div className="mb-3 text-xs text-[#6B7280]">
            <p className="mb-1">
              Payments found on this statement date (not auto-applied — confirm before adding):
            </p>
            <div className="flex flex-wrap gap-2">
              {candidatePayments.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`invoice-gen-candidate-payment-${i}`}
                  onClick={() =>
                    addPayment({ date: p.date, details: `Payment #${p.payment_no} - ${p.method || ""}`.trim(), amount: p.amount })
                  }
                  className="border border-[#D1D5DB] rounded-full px-3 py-1 hover:border-[#3A7A8C]"
                >
                  + Payment #{p.payment_no} · {money(p.amount)} · {p.method}
                </button>
              ))}
            </div>
          </div>
        )}
        {draft.payments.length === 0 ? (
          <p className="text-sm text-[#9CA3AF]">No payments added.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {draft.payments.map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid={`invoice-payment-${idx}`}>
                <input className="ash-input" placeholder="Date" value={p.date || ""} onChange={(e) => updatePayment(idx, "date", e.target.value)} data-testid={`invoice-payment-${idx}-date`} />
                <input className="ash-input" placeholder="Details" value={p.details || ""} onChange={(e) => updatePayment(idx, "details", e.target.value)} data-testid={`invoice-payment-${idx}-details`} />
                <input type="number" step="0.01" className="ash-input" placeholder="Amount" value={p.amount ?? ""} onChange={(e) => updatePayment(idx, "amount", parseFloat(e.target.value) || 0)} data-testid={`invoice-payment-${idx}-amount`} />
                <button type="button" onClick={() => removePayment(idx)} data-testid={`invoice-payment-${idx}-remove`} className="inline-flex items-center gap-1 text-xs text-[#B91C1C] hover:underline">
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-[#E5E7EB] pt-4" data-testid="invoice-gen-totals">
        <div className="flex justify-end gap-8 text-sm">
          <div className="text-right">
            <p className="text-[#6B7280]">Subtotal</p>
            <p className="text-[#6B7280]">Tax</p>
            <p className="font-bold text-[#080808]">Total</p>
            <p className="text-[#6B7280]">Paid</p>
            <p className="font-semibold">Balance</p>
          </div>
          <div className="text-right">
            <p>{money(totals.subtotal)}</p>
            <p>{money(totals.taxTotal)}</p>
            <p className="font-bold text-[#080808]">{money(totals.total)}</p>
            <p>{money(totals.paid)}</p>
            <p className="font-semibold">{money(totals.balance)}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
